// The Moon's phase, age and distance, and the times of its four main phases,
// for the Moon phase pages (/moon-phase/, its month calendars,
// /full-moon/<year>/) and the Moon's own page (/moons/moon/, through
// src/site/readouts.js). Pure, no DOM: the build bakes these values and the
// browser runs the same code (src/site/moonpage.js, src/site/live.js).
//
// One ephemeris for everything: the Moon from Meeus's lunar theory
// (src/orbits/moon.js, Astronomical Algorithms ch. 47, the series the viewer
// places the Moon with) and the Sun from JPL's elements for the Earth-Moon
// barycentre (src/orbits/ephemeris.js), moved to the Earth's centre. The
// phase, the lit fraction, the age and the distance all come from those two.
//
// The phase times follow the US Naval Observatory's definition: the moment
// the Moon's apparent ecliptic longitude is 0, 90, 180 or 270 degrees past
// the Sun's. Three corrections bring the times from about two minutes late
// (src/sky/timejumps.js, which has none of them) to within a minute of
// USNO's (docs/moon-phase-verification.md, tests/moon-phase.test.js):
//   - both ephemerides run on Terrestrial Time, 69.184 s ahead of UTC
//     (TT_MINUS_UTC_S, src/eclipse/geometry.js);
//   - the Sun is seen 20.5 arcseconds back along its path by aberration
//     (Meeus ch. 25: -20.4898" / R, with R in AU);
//   - the Sun is seen from the Earth's centre, not from the barycentre.

import { moonEcliptic } from '../orbits/moon.js';
import { getPlanetPosition } from '../orbits/ephemeris.js';
import { TT_MINUS_UTC_S } from '../eclipse/geometry.js';

const DEG = Math.PI / 180;
const J2000 = 2451545.0;
const AU_KM = 149597870.7;
const TT = TT_MINUS_UTC_S / 86400;
// Moon mass / (Earth + Moon mass): how far the Earth sits from the
// barycentre, as a share of the Moon's distance (as readouts.js uses it).
const MOON_MASS_SHARE = 1 / 82.30057;
const ABERRATION = (20.4898 / 3600) * DEG;
// General precession in longitude, J2000 to the date, per Julian century.
const PRECESSION = 1.3969713 * DEG;
const MINUTE = 1 / 1440;

const wrap360 = (d) => ((d % 360) + 360) % 360;
const wrap180 = (d) => ((((d + 180) % 360) + 360) % 360) - 180;

// The four main phases, by the Moon's elongation east of the Sun.
export const MAIN_PHASES = [
  { key: 'new', deg: 0, name: 'New Moon' },
  { key: 'first', deg: 90, name: 'First quarter' },
  { key: 'full', deg: 180, name: 'Full Moon' },
  { key: 'last', deg: 270, name: 'Last quarter' },
];

// The Moon and the Sun seen from the Earth's centre at a UTC Julian date:
// elong, the Moon's apparent longitude east of the Sun's (degrees, 0..360);
// lit, the share of the disc in sunlight (USNO's "fraction illuminated");
// phaseAngle, the Sun-Moon-Earth angle (degrees); km, the Moon's distance,
// centre to centre.
export function moonGeometry(jdUtc) {
  const jd = jdUtc + TT;
  const T = (jd - J2000) / 36525;
  const m = moonEcliptic(jd);
  // The Earth's centre, heliocentric, J2000, km. The Moon's offset is turned
  // back to J2000 by the precession; its 6.5 arcsecond pull hardly needs it.
  const b = getPlanetPosition('earth', jd);
  const lonJ2000 = m.lon - PRECESSION * T;
  const cl = Math.cos(m.lat);
  const k = MOON_MASS_SHARE * m.km;
  const ex = b.x * AU_KM - k * cl * Math.cos(lonJ2000);
  const ey = b.y * AU_KM - k * cl * Math.sin(lonJ2000);
  const ez = b.z * AU_KM - k * Math.sin(m.lat);
  const sunKm = Math.hypot(ex, ey, ez);
  // The Sun's apparent longitude of date: opposite the Earth, precessed to
  // the date, and moved back by aberration.
  const sunLon = Math.atan2(-ey, -ex) + PRECESSION * T - ABERRATION / (sunKm / AU_KM);
  const dLon = m.lon - sunLon;
  // Meeus 48.2 and 48.3: the elongation from the Sun, then the phase angle
  // with the Moon's distance against the Sun's.
  const psi = Math.acos(Math.max(-1, Math.min(1, Math.cos(m.lat) * Math.cos(dLon))));
  const i = Math.atan2(sunKm * Math.sin(psi), m.km - sunKm * Math.cos(psi));
  return {
    elong: wrap360(dLon / DEG),
    lit: (1 + Math.cos(i)) / 2,
    phaseAngle: i / DEG,
    km: m.km,
  };
}

// The first moment strictly after jd when the elongation reaches deg (0 new,
// 90 first quarter, 180 full, 270 last quarter): daily steps, then bisection
// to well under a second. Null if none within 33 days, which cannot happen.
export function nextPhaseJd(jd, deg) {
  const f = (t) => wrap180(moonGeometry(t).elong - deg);
  let a = jd + MINUTE / 60;
  let fa = f(a);
  for (let t = a + 1; t <= jd + 33; t += 1) {
    const fb = f(t);
    // A rise through zero; the wrap from +180 to -180 is a fall.
    if (fa < 0 && fb >= 0 && fb - fa < 90) {
      let lo = a, hi = t;
      for (let n = 0; n < 40; n++) {
        const mid = (lo + hi) / 2;
        if (f(mid) < 0) lo = mid; else hi = mid;
      }
      return (lo + hi) / 2;
    }
    a = t;
    fa = fb;
  }
  return null;
}

// The last moment at or before jd when the elongation reached deg.
export function previousPhaseJd(jd, deg) {
  let t = nextPhaseJd(jd - 31, deg);
  for (;;) {
    const n = nextPhaseJd(t, deg);
    if (n == null || n > jd) return t;
    t = n;
  }
}

// Every main phase from jd0 up to (not including) jd1, in order:
// [{ key, name, deg, jd }].
export function mainPhasesBetween(jd0, jd1) {
  const out = [];
  for (const p of MAIN_PHASES) {
    let t = nextPhaseJd(jd0 - MINUTE, p.deg);
    while (t != null && t < jd1) {
      if (t >= jd0) out.push({ ...p, jd: t });
      t = nextPhaseJd(t, p.deg);
    }
  }
  return out.sort((a, b) => a.jd - b.jd);
}

// The next occurrence of each main phase after jd, soonest first.
export function nextMainPhases(jd) {
  return MAIN_PHASES.map((p) => ({ ...p, jd: nextPhaseJd(jd, p.deg) })).sort((a, b) => a.jd - b.jd);
}

// The name for the elongation alone: the four main phases are moments, so
// each names the day around it (the Moon gains about 12 degrees a day on the
// Sun, so 6 degrees either side), with crescent and gibbous between.
export function phaseName(elong) {
  const near = (target) => Math.abs(wrap180(elong - target)) < 6;
  if (near(0)) return 'New Moon';
  if (near(90)) return 'First quarter';
  if (near(180)) return 'Full Moon';
  if (near(270)) return 'Last quarter';
  return elong < 90 ? 'Waxing crescent' : elong < 180 ? 'Waxing gibbous' : elong < 270 ? 'Waning gibbous' : 'Waning crescent';
}

// The name between the main phases, for a calendar day that holds none.
export function betweenName(elong) {
  return elong < 90 ? 'Waxing crescent' : elong < 180 ? 'Waxing gibbous' : elong < 270 ? 'Waning gibbous' : 'Waning crescent';
}

// The Moon at a moment: everything the "Moon today" readouts show, plus its
// age, USNO's "time in days counted from the time of New Moon".
export function moonNow(jd) {
  const g = moonGeometry(jd);
  const lastNew = previousPhaseJd(jd, 0);
  return { ...g, name: phaseName(g.elong), waxing: g.elong < 180, age: jd - lastNew, lastNew };
}

// ---- Perigee, apogee and supermoons ------------------------------------------------

const distanceKm = (jd) => moonEcliptic(jd + TT).km;

// Every perigee ('min') or apogee ('max') of the Moon between jd0 and jd1:
// [{ jd, km }]. Half-day steps, then golden-section search.
export function apsidesBetween(jd0, jd1, kind) {
  const sign = kind === 'max' ? -1 : 1;
  const d = (t) => sign * distanceKm(t);
  const out = [];
  const step = 0.5;
  let a = d(jd0 - step), b = d(jd0);
  for (let t = jd0; t < jd1; t += step) {
    const c = d(t + step);
    if (b <= a && b < c) {
      let lo = t - step, hi = t + step;
      const g = (Math.sqrt(5) - 1) / 2;
      for (let n = 0; n < 50; n++) {
        const x1 = hi - g * (hi - lo), x2 = lo + g * (hi - lo);
        if (d(x1) < d(x2)) hi = x2; else lo = x1;
      }
      const at = (lo + hi) / 2;
      out.push({ jd: at, km: distanceKm(at) });
    }
    a = b;
    b = c;
  }
  return out;
}

const nearest = (list, jd) => list.reduce((best, x) => (!best || Math.abs(x.jd - jd) < Math.abs(best.jd - jd) ? x : best), null);

// Fred Espenak's test for a full Moon at perigee (a "full supermoon"),
// Astropixels, "Full Moon at Perigee: 2001 to 2100": Nolle's "within 90% of
// its closest approach to Earth in a given orbit", measured against the
// apogee and the perigee either side of the full Moon as
// (Da - Dm) / (Da - Dp). 0.9 or more is a supermoon. We take the apogee and
// the perigee nearest in time.
export const SUPERMOON_THRESHOLD = 0.9;
export function supermoonAt(jd) {
  const km = distanceKm(jd);
  const perigee = nearest(apsidesBetween(jd - 16, jd + 16, 'min'), jd);
  const apogee = nearest(apsidesBetween(jd - 16, jd + 16, 'max'), jd);
  const relative = (apogee.km - km) / (apogee.km - perigee.km);
  return { km, perigee, apogee, relative, supermoon: relative >= SUPERMOON_THRESHOLD };
}

// ---- Drawing --------------------------------------------------------------------------

// The lit part of a disc of radius r centred on 0,0, as an SVG path, seen
// from the Northern Hemisphere (lit on the right while waxing): the limb on
// the lit side, then the terminator, a half ellipse whose width follows the
// lit fraction.
export function phasePath(lit, waxing, r) {
  const rx = Math.abs(1 - 2 * lit) * r;
  const limb = waxing ? 1 : 0;
  const term = (lit < 0.5) === waxing ? 0 : 1;
  return `M0 ${-r}A${r} ${r} 0 0 ${limb} 0 ${r}A${rx.toFixed(2)} ${r} 0 0 ${term} 0 ${-r}Z`;
}

// The screensaver framing (src/screensaver/tour.js) that shows the Moon lit
// as it is from the Earth at this moment: the camera on the Earth's side of
// the Moon, its angle from the sunward direction being the elongation less
// 180 degrees, ecliptic north up. Encoded for ?shot= as tour.js decodes it.
export function earthViewShot(elong, radii = 3.2) {
  const phase = (wrap180(elong - 180) * Math.PI) / 180;
  return { id: 'moon', name: 'moon-from-earth', radii, phase: Number(phase.toFixed(4)), elev: 0, orbit: 0 };
}
